import { BODY_SURFACE_GLSL } from './body-surface';
import { PHOTOSPHERE_GLSL } from './photosphere';
import { ECLIPSE_OVERLAP_GLSL } from './eclipse';
import { NOISE_GLSL } from './noise';
import { BODY_MATERIAL_GLSL } from './body-material';
import { SOLAR_ACTIVITY_GLSL } from './solar-activity';
import {
  BODY_SEASON_PHASES,
  BODY_SPIN_OFFSETS,
  BODY_SPIN_RATES,
} from '../../simulation/celestial-environment';
import { SURFACE_COORDINATE_GLSL } from '../../simulation/surface-coordinates';

function glslBodyLookup(name: string, values: readonly number[]): string {
  const branches = values.map((value, slot) => {
    const literal = Number.isInteger(value) ? `${value}.0` : String(value);
    const keyword = slot === 0 ? 'if' : 'else if';
    return `  ${keyword} (bodyIndex < ${slot + 1}.5) return ${literal};`;
  });
  return `float ${name}(float bodyIndex) {\n${branches.join('\n')}\n  return 0.0;\n}`;
}

const BODY_SPIN_RATE_GLSL = glslBodyLookup('bodySpinRate', BODY_SPIN_RATES);
const BODY_SPIN_OFFSET_GLSL = glslBodyLookup(
  'bodySpinOffset',
  BODY_SPIN_OFFSETS,
);
const BODY_SEASON_PHASE_GLSL = glslBodyLookup(
  'bodySeasonPhase',
  BODY_SEASON_PHASES,
);

export const PRIMARY_FRAGMENT_SHADER = `#version 300 es
precision highp float;

in float v_kind;
in float v_massSignal;
in vec2 v_position;
in vec2 v_spriteCoord;
flat in float v_stellarVisibility;
flat in float v_bodyIndex;
flat in vec4 v_bodyMaterial;
flat in vec4 v_bodyClimate;
flat in vec3 v_bodyIceColour;
flat in vec3 v_bodyCloudColour;
flat in vec4 v_bodyLowColor;
flat in vec4 v_bodyHighColor;
flat in vec4 v_bodyLiquidColor;
flat in vec4 v_bodyAtmosphereColor;
out vec4 outColor;
uniform float u_planetVegetation[512];
uniform float u_interpolation;
uniform float u_simulationTime;
uniform vec2 u_starPosition;
uniform vec2 u_previousPlanetPositions[16];
uniform vec2 u_currentPlanetPositions[16];
uniform float u_planetRadii[16];

${SURFACE_COORDINATE_GLSL}

${ECLIPSE_OVERLAP_GLSL}
float stellarVisibility(vec2 targetPosition, float excludedBodyIndex) {
  vec2 targetFromStar = targetPosition - u_starPosition;
  float targetDistance = length(targetFromStar);
  if (targetDistance <= STAR_LIGHT_RADIUS) {
    return 1.0;
  }
  vec2 rayDirection = targetFromStar / targetDistance;
  float visibility = 1.0;
  for (int slot = 0; slot < 16; slot += 1) {
    float bodyIndex = float(slot + 1);
    if (abs(excludedBodyIndex - bodyIndex) < 0.25) {
      continue;
    }
    vec2 occluderPosition = mix(
      u_previousPlanetPositions[slot],
      u_currentPlanetPositions[slot],
      u_interpolation
    );
    vec2 occluderFromStar = occluderPosition - u_starPosition;
    float alongRay = dot(occluderFromStar, rayDirection);
    if (alongRay <= STAR_LIGHT_RADIUS || alongRay >= targetDistance) {
      continue;
    }
    vec2 targetToOccluder = occluderPosition - targetPosition;
    float occluderDistance = max(0.0001, length(targetToOccluder));
    float angularSeparation = acos(clamp(
      dot(-rayDirection, targetToOccluder / occluderDistance),
      -1.0,
      1.0
    ));
    float coverage = circleOverlapFraction(
      STAR_LIGHT_RADIUS / targetDistance,
      u_planetRadii[slot] / occluderDistance,
      angularSeparation
    );
    visibility = min(visibility, 1.0 - coverage * 0.98);
  }
  return visibility;
}

${NOISE_GLSL}
vec3 planetSpinAxis(float bodyIndex) {
  float tilt = 0.12 + bodyIndex * 0.09;
  float depth = 0.14;
  if (bodyIndex > 1.5 && bodyIndex < 2.5) tilt = 3.02;
  else if (bodyIndex > 2.5 && bodyIndex < 3.5) tilt = 0.4084;
  else if (bodyIndex > 6.5 && bodyIndex < 7.5) tilt = 1.71;
  else if (bodyIndex > 7.5) tilt = 0.49;
  return normalize(vec3(sin(tilt), cos(tilt), depth));
}

${BODY_SPIN_RATE_GLSL}
${BODY_SPIN_OFFSET_GLSL}
${BODY_SEASON_PHASE_GLSL}

float planetSpinAngle(float bodyIndex) {
  return u_simulationTime * bodySpinRate(bodyIndex) + bodySpinOffset(bodyIndex);
}

vec3 toBodyNormal(vec3 worldNormal, vec3 spinAxis, float spinAngle) {
  vec3 reference =
    abs(spinAxis.z) < 0.97 ? vec3(0.0, 0.0, 1.0) : vec3(0.0, 1.0, 0.0);
  vec3 equatorX = normalize(cross(reference, spinAxis));
  vec3 equatorZ = normalize(cross(spinAxis, equatorX));
  float cosine = cos(spinAngle);
  float sine = sin(spinAngle);
  vec3 longitudeX = equatorX * cosine + equatorZ * sine;
  vec3 longitudeZ = equatorZ * cosine - equatorX * sine;
  return normalize(vec3(
    dot(worldNormal, longitudeX),
    dot(worldNormal, spinAxis),
    dot(worldNormal, longitudeZ)
  ));
}

${BODY_MATERIAL_GLSL}
${SOLAR_ACTIVITY_GLSL}
void main() {
  vec2 centered = v_spriteCoord - 0.5;
  float radius = length(centered);
  float alpha = 0.0;
  vec3 color = vec3(0.0);

  if (v_kind < 1.5) {
    discard;
  } else if (v_kind < 2.5) {
    float fish = abs(centered.x * 0.72) + abs(centered.y * 1.35);
    alpha = (1.0 - smoothstep(0.16, 0.56, fish)) * 0.94;
    color = vec3(0.76, 0.98, 1.0);
  } else if (v_kind < 3.5) {
    // Predator appearance is deliberately independent of internal behavior.
    float diamond = abs(centered.x) + abs(centered.y);
    float core = 1.0 - smoothstep(0.05, 0.34, diamond);
    float edge = 1.0 - smoothstep(0.20, 0.60, diamond);
    alpha = edge;
    color =
      mix(vec3(0.92, 0.16, 0.035), vec3(1.0, 0.63, 0.12), core) *
      (0.78 + core * 0.34);
  } else if (v_kind < 4.5) {
${PHOTOSPHERE_GLSL}
  } else if (v_kind < 6.5) {
${BODY_SURFACE_GLSL}
  } else if (v_kind < 7.5) {
    float remnant = max(
      abs(centered.x * 0.82) + abs(centered.y * 1.28),
      length(centered + vec2(0.08, -0.04)) * 0.74
    );
    alpha = (1.0 - smoothstep(0.14, 0.48, remnant)) * 0.84;
    color = mix(vec3(0.30, 0.14, 0.10), vec3(0.78, 0.34, 0.16), 0.44);
  } else if (v_kind < 8.5) {
    float kite =
      abs(centered.x * 0.72) +
      abs(centered.y * 1.08) +
      max(0.0, -centered.x) * 0.24;
    alpha = (1.0 - smoothstep(0.13, 0.56, kite)) * 0.97;
    color = mix(vec3(0.66, 0.40, 1.0), vec3(0.96, 0.76, 1.0), 0.48);
  } else if (v_kind < 9.5) {
    vec2 inward = normalize(u_starPosition - v_position + vec2(0.0001, 0.0));
    vec2 tangent = vec2(-inward.y, inward.x);
    float along = dot(centered, inward);
    float across = dot(centered, tangent);
    float head = 1.0 - smoothstep(
      0.04,
      0.18,
      length(vec2(along - 0.16, across))
    );
    float tailWidth = mix(0.03, 0.17, smoothstep(-0.48, 0.08, along));
    float tail =
      (1.0 - smoothstep(tailWidth, tailWidth + 0.06, abs(across))) *
      (1.0 - smoothstep(-0.48, 0.18, along)) *
      smoothstep(-0.52, -0.08, along);
    float halo = 1.0 - smoothstep(
      0.08,
      0.31,
      length(vec2(along - 0.15, across))
    );
    alpha = max(head, max(tail * 0.72, halo * 0.32));
    color =
      vec3(0.72, 0.92, 1.0) * tail +
      vec3(0.55, 0.86, 1.0) * halo +
      vec3(1.0, 1.0, 0.92) * head * 1.35;
  } else {
    float angle = atan(centered.y, centered.x);
    float roughEdge = 0.40 + sin(angle * 5.0 + v_massSignal * 8.0) * 0.055;
    alpha = 1.0 - smoothstep(
      roughEdge - 0.08,
      roughEdge,
      length(centered)
    );
    color = mix(
      vec3(0.58, 0.61, 0.62),
      vec3(1.0, 0.86, 0.62),
      fract(v_bodyIndex * 0.6180339)
    );
  }

  bool isLitParticle =
    (v_kind >= 1.5 && v_kind < 3.5) || v_kind >= 6.5;
  if (isLitParticle) {
    vec2 starDirection = normalize(
      u_starPosition - v_position + vec2(0.000001, 0.0)
    );
    vec2 localPoint = clamp(centered * 2.0, vec2(-1.0), vec2(1.0));
    float localZ = sqrt(max(0.0, 1.0 - dot(localPoint, localPoint)));
    vec3 particleNormal = normalize(vec3(localPoint, localZ));
    vec3 particleLightDirection = normalize(vec3(starDirection, 0.34));
    float particleDiffuse = max(
      0.0,
      dot(particleNormal, particleLightDirection)
    );
    color *=
      (0.24 + particleDiffuse * 0.94) *
      mix(0.08, 1.0, v_stellarVisibility);
  }

  if (alpha < 0.008) {
    discard;
  }
  outColor = vec4(color * alpha, alpha);
}`;
